import { isNative } from './platform'
import localforage from 'localforage'
import { uploadPodMultipart } from './api'
import type { CourierScope } from './courierScope'
import { getCourierScope } from './courierScope'

export type QueueItem = {
  id: string
  deliveryId: string
  createdAt: number
  scope?: CourierScope | null
  delivery?: {
    invoiceNumber?: string
    code?: string
    recipientName: string
  }
  payload: {
    status: 'delivered' | 'failed' | 'partial'
    observations?: string
    received_by_name?: string
    received_by_email?: string
    received_by_document?: string
    signed_at: string
    location?: [number, number]
    geo?: { lat: number; lng: number }
    images?: string[]
    image?: string
    signature?: string
    conference?: Array<{
      id?: string
      position: number
      product_code: string
      description: string
      expected_quantity: string
      delivered_quantity: string
      unit: string
    }>
  }
}

const store = localforage.createInstance({ name: 'canhoto-queue' })
const queueKey = 'pod-queue'
const subscribers = new Set<() => void>()

async function getQueue(): Promise<QueueItem[]> {
  return (await store.getItem<QueueItem[]>(queueKey)) ?? []
}

async function setQueue(queue: QueueItem[]): Promise<void> {
  await store.setItem(queueKey, queue)
  for (const subscriber of subscribers) subscriber()
}

export function subscribeQueue(subscriber: () => void): () => void {
  subscribers.add(subscriber)
  return () => subscribers.delete(subscriber)
}

function sameScope(left?: CourierScope | null, right?: CourierScope | null): boolean {
  if (!left || !right || left.userId !== right.userId) return false
  return left.membershipIds.slice().sort().join(',') === right.membershipIds.slice().sort().join(',')
}

export async function getPendingItems(scope: CourierScope | null = getCourierScope()): Promise<QueueItem[]> {
  if (!scope) return []
  return (await getQueue()).filter(item => sameScope(item.scope, scope))
}

export async function getPendingCount(scope: CourierScope | null = getCourierScope()): Promise<number> {
  return (await getPendingItems(scope)).length
}

export async function enqueuePOD(
  deliveryId: string,
  payload: QueueItem['payload'],
  scope: CourierScope | null,
  delivery?: QueueItem['delivery'],
): Promise<QueueItem> {
  const queue = await getQueue()
  const normalized = {
    ...payload,
    images: payload.images ?? (payload.image ? [payload.image] : undefined),
  }
  const pending = scope ? queue.filter(item => item.deliveryId === deliveryId && sameScope(item.scope, scope)) : []
  if (pending.length > 0) {
    const kept = pending[0]
    const updated: QueueItem = { ...kept, delivery: delivery ?? kept.delivery, payload: normalized }
    await setQueue(queue.map(item => item.id === kept.id ? updated : item).filter(item => item.id === kept.id || !pending.some(match => match.id === item.id)))
    void ensureBackgroundSync()
    return updated
  }
  const item: QueueItem = {
    id: crypto.randomUUID(),
    deliveryId,
    createdAt: Date.now(),
    scope,
    delivery,
    payload: normalized,
  }
  await setQueue([...queue, item])
  void ensureBackgroundSync()
  return item
}

export async function ensureBackgroundSync(): Promise<void> {
  try {
    if (isNative() || !('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.ready
    const syncRegistration = registration as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> }
    }
    if (syncRegistration.sync) await syncRegistration.sync.register('pod-sync')
    else navigator.serviceWorker.controller?.postMessage({ type: 'REQUEST_POD_SYNC' })
  } catch {
    // Synchronization is also retried when the app becomes visible or online.
  }
}

let activeProcessing: Promise<void> | null = null

export function processQueue(scope: CourierScope | null = getCourierScope()): Promise<void> {
  if (!scope) return Promise.resolve()
  if (activeProcessing) return activeProcessing
  activeProcessing = processQueueOnce(scope).finally(() => { activeProcessing = null })
  return activeProcessing
}

async function processQueueOnce(scope: CourierScope): Promise<void> {
  const processItems = async () => {
    for (const item of await getPendingItems(scope)) {
      try {
        await uploadPodMultipart(item.deliveryId, createPodForm(item))
        await setQueue((await getQueue()).filter(queued => queued.id !== item.id))
      } catch {
        // Keep all evidence in IndexedDB for the next retry.
      }
    }
    if ((await getQueue()).length > 0) void ensureBackgroundSync()
  }

  if ('locks' in navigator && navigator.locks) {
    await navigator.locks.request('canhoto-pod-queue-sync', processItems)
  } else {
    await processItems()
  }
}

function createPodForm(item: QueueItem): FormData {
  const form = new FormData()
  form.append('delivery', item.deliveryId)
  form.append('idempotency_key', item.id)
  form.append('status', item.payload.status)
  form.append('outcome', item.payload.status === 'failed' ? 'failed' : 'delivery')
  if (item.payload.status !== 'failed') {
    item.payload.conference?.filter(line => line.id).forEach((line, index) => {
      form.append(`items[${index}]item_id`, line.id as string)
      form.append(`items[${index}]delivered_quantity`, line.delivered_quantity)
      form.append(`items[${index}]confirmed`, 'true')
    })
  }
  form.append('signed_at', item.payload.signed_at)
  if (item.payload.observations) form.append('observations', item.payload.observations)
  if (item.payload.received_by_name) form.append('received_by_name', item.payload.received_by_name)
  if (item.payload.received_by_email) form.append('received_by_email', item.payload.received_by_email)
  if (item.payload.received_by_document) form.append('received_by_document', item.payload.received_by_document)
  const location = item.payload.location
    ?? (item.payload.geo ? [item.payload.geo.lng, item.payload.geo.lat] as [number, number] : undefined)
  if (location) form.append('location', JSON.stringify(location))
  form.append('meta', JSON.stringify({
    source: 'offlineQueue',
    timestamp: new Date().toISOString(),
    ...(item.payload.conference ? { conference: item.payload.conference } : {}),
  }))

  if (item.payload.signature) {
    form.append('signature_image', dataUrlToBlob(item.payload.signature), 'signature.png')
  }
  const images = item.payload.images ?? (item.payload.image ? [item.payload.image] : [])
  images.forEach((image, index) => {
    form.append('photos', dataUrlToBlob(image), `photo_${index + 1}.jpg`)
  })
  return form
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [metadata, encodedData = ''] = dataUrl.split(',', 2)
  const mimeType = metadata.match(/:(.*?);/)?.[1] ?? 'application/octet-stream'
  const decodedData = atob(encodedData)
  const bytes = new Uint8Array(decodedData.length)
  for (let index = 0; index < decodedData.length; index += 1) {
    bytes[index] = decodedData.charCodeAt(index)
  }
  return new Blob([bytes], { type: mimeType })
}
